import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';

import * as api from './api';
import type { ImportDestination, ImportJob } from './types';

/** Poll one import job; auto-stops once it reaches a terminal status. */
export function useImportJob(publicId: string | null) {
  return useQuery({
    queryKey: ['import-job', publicId],
    queryFn: () => api.getImportJob(publicId as string),
    enabled: !!publicId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? 1500 : false;
    },
  });
}

export interface StartImportVars {
  file: File;
  collectionName?: string;
  parentTitle?: string;
  skipHomePage?: boolean;
}

export function useStartImport(destination: ImportDestination) {
  return useMutation<ImportJob, ApiError, StartImportVars>({
    mutationFn: ({ file, collectionName, parentTitle, skipHomePage }) =>
      destination.kind === 'wiki'
        ? api.startWikiImport(file, { collectionName, parentTitle, skipHomePage })
        : api.startProjectImport(destination.projectPublicId, file, { parentTitle, skipHomePage }),
    onError: (err) => toast.error(err.message || 'Import failed to start.'),
  });
}
